import { ApiService } from 'src/app/services/api/api.service';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { SignupService } from 'src/app/services/signup/signup.service';
import fakerbr from 'faker-br';
import { distinctUntilChanged } from 'rxjs/operators';
import Utils, { GetHelper, payloadsEqual, UtilsValidators } from 'src/app/helpers/utils';
import { ToastService } from 'src/app/services/toast/toast.service';
import { MaskPipe } from 'ngx-mask';
import { mainActivityItems, riskItems, webCopItems } from 'src/app/data/risk';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-signup-unified-person-complementary-risk',
  templateUrl: './signup-unified-person-complementary-risk.component.html',
  styleUrls: ['./signup-unified-person-complementary-risk.component.scss'],
})
export class SignupUnifiedPersonComplementaryRiskComponent implements OnInit {
  @Input() userRole: string;

  form = this.formBuilder.group({
    operating_limit: [null, [Validators.required, Validators.min(1)]],
    risk: [null, [Validators.required]],
    observations: [null, []],
  });

  loading = true;
  riskId = null;

  /** Snapshot do payload após o GET; usado para evitar PATCH/PUT quando nada mudou. */
  private initialPayload: Record<string, any> | null = null;

  event: any;

  riskOptions = riskItems;
  webCopOptions = webCopItems;
  mainAcityOptions = mainActivityItems;

  constructor(
    private formBuilder: FormBuilder,
    public signupService: SignupService,
    private api: ApiService,
    private toast: ToastService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnDestroy(): void {
    this.event.unsubscribe();
  }

  ngOnInit(): void {
    // Verificar se é cedente ou prestador e redirecionar se necessário
    const isAssignor = this.router.url.includes('assignor');
    const isProvider = this.router.url.includes('provider');

    if (isAssignor || isProvider) {
      // Se for cedente ou prestador, redireciona para representantes
      this.signupService.setPersonStep('complementaryRepresentatives');
      this.signupService.changePersonStepEvent.emit('representatives');
      return;
    }

    // GARANTIR QUE O STEP ESTÁ SETADO CORRETAMENTE
    this.signupService.setPersonStep('complementaryRisk');

    this.signupService.choicesFetchedEvent.subscribe((choices) => {
      this.loading = false;
    });

    this.event = this.signupService.sendPersonData.subscribe((value) => {
      if (value === 'complementaryRisk') {
        this.sendData();
      }
    });

    this.getDataAndFillForm();

    // Listener para mudanças de valor
    this.form.valueChanges.subscribe(() => {
      console.log(Utils.getErrors(this.form));

      this.signupService.setPersonFormStatus(
        'complementaryRisk',
        this.form.valid
      );
    });
  }

  async getDataAndFillForm() {
    this.loading = true;
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        this.toast.show(
          'error',
          'Erro',
          'Register UUID não encontrado na rota'
        );
        this.loading = false;
        return;
      }

      const res = await this.api.get<any>({
        route: 'api/registration/risk-information/',
        token: true,
        params: {
          register_uuid: routeId,
        },
      });

      // A resposta pode vir como array ou objeto único
      const riskData = Array.isArray(res) ? res[0] : res.data || res;

      if (riskData) {
        this.riskId = riskData.id ?? null;

        this.form.patchValue({
          operating_limit: riskData.operating_limit,
          risk: riskData.risk,
          observations: riskData.observations,
        });

        this.initialPayload = this.buildPayloadFromForm();
      } else {
        this.initialPayload = null;
      }

      // Atualiza o status do formulário após o Angular processar o patchValue
      // Usa setTimeout para garantir que a validação seja executada
      setTimeout(() => {
        // Força atualização da validação de todos os controles
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].updateValueAndValidity({ emitEvent: false });
        });

        const isValid = this.form.valid;
        this.signupService.setPersonFormStatus('complementaryRisk', isValid);
      }, 0);
    } catch (error) {
      this.initialPayload = null;
      // Em caso de erro, também atualiza o status
      setTimeout(() => {
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].updateValueAndValidity({ emitEvent: false });
        });
        this.signupService.setPersonFormStatus('complementaryRisk', this.form.valid);
      }, 0);
    } finally {
      this.loading = false;
    }
  }

  private buildPayloadFromForm(): Record<string, any> {
    const valuesHelper = new GetHelper(this.form.value);
    return {
      ...valuesHelper.getRemainingObject(),
    };
  }

  async sendData() {
    const routeId = this.route.snapshot.paramMap.get('id');

    if (!routeId) {
      this.toast.show(
        'error',
        'Erro',
        'Register UUID não encontrado na rota'
      );
      return;
    }

    const payload = this.buildPayloadFromForm();

    if (payloadsEqual(payload, this.initialPayload ?? undefined)) {
      this.signupService.loading = false;
      this.signupService.setPersonStep('complementaryRepresentatives');
      this.signupService.changePersonStepEvent.emit('representatives');
      return;
    }

    this.signupService.loading = true;

    try {
      let apiRoute: string;
      const params: any = { register_uuid: routeId };

      if (this.riskId) {
        apiRoute = `api/registration/risk-information/${this.riskId}/`;

        const response = await this.api.put<any>({
          route: apiRoute,
          params: params,
          token: true,
          body: payload,
        });

        this.initialPayload = this.buildPayloadFromForm();
        this.toast.show(
          'info',
          'Sucesso',
          response.message || 'Dados salvos com sucesso!'
        );
      } else {
        apiRoute = 'api/registration/risk-information/';

        const response = await this.api.post<any>({
          route: apiRoute,
          params: params,
          token: true,
          body: payload,
        });

        this.riskId = response.data?.id ?? null;
        this.initialPayload = this.buildPayloadFromForm();
        this.toast.show(
          'info',
          'Sucesso',
          response.message || 'Dados salvos com sucesso!'
        );
      }

      this.signupService.setPersonStep('complementaryRepresentatives');
      this.signupService.changePersonStepEvent.emit('representatives');
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Ocorreu um erro ao salvar os dados'
      );
      this.signupService.loading = false;
    } finally {
      this.signupService.loading = false;
    }
  }
}
